import React, { useMemo, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import RoleToggle from "../../components/RoleToggle";
import NoDataFound from "../../components/common/NoDataFound";
import AllOrders from "./_components/AllOrders";
import { IconX } from "@tabler/icons-react";
import CreateOrder from "../CreateOrder";
import CreatedOrders from "../CreatedOrders";
import PickListPage from "../PickList";
import Packaging from "../Packaging";
import Dispatched from "../Dispatched";
import PendingOrders from "./_components/PendingOrders";
import { usePermissionTabs } from "../../hooks/usePermissionTabs";
import { PermissionTabManager } from "../../components/PermissionTabManager";

type ClientType = "FM" | "LM" | "BOTH" | null;

interface Tab {
  key: string;
  label: string;
  component: React.ReactNode;
}

const FILTER_LABEL_MAP: Record<string, string> = {
  awbNumber: "Search",
  referenceNumber: "Search",
  mobileNumber: "Search",
  seller: "Seller",
  warehouseName: "Warehouse",
  paymentType: "Payment Type",
  codAmount: "COD Amount",
  sameDate: "Same Date",
  entryDate: "Entry Date",
  closeDate: "Close Date",
  senderCityName: "Origin City",
  senderCountryName: "Origin Country",
  receiverCityName: "Destination City",
  receiverCountryName: "Destination Country",
  receiverDistrictName: "Destination District",
  city: "City",
  orderType: "Order Type",
  serviceType: "Order Type",
  platform: "Platform",
  shippingCompany: "Shipping Company",
  shippingAwbNumber: "Shipping AWB",
  shippingType: "Shipping Type",
  entryDateFrom: "Entry Date",
  entryDateTo: "Entry Date",
  closeDateFrom: "Close Date",
  closeDateTo: "Close Date",
  status: "Status",
};

const OrderManagement: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useSelector((state: any) => state.auth.profile);
  const clientType = profile?.clientType as ClientType;

  const [filters, setFilters] = useState<Array<Record<string, string>>>([]);
  const [headerActions, setHeaderActions] = useState<React.ReactNode>(null);
  const [headerButtons, setHeaderButtons] = useState<React.ReactNode>(null);
  const [pickListRefresh, setPickListRefresh] = useState(0);

  const ALL_TABS = useMemo(() => [
    {
      key: "Orders",
      label: "Orders",
      component: (
        <CreateOrder
          setHeaderActions={setHeaderActions}
          setHeaderButtons={setHeaderButtons}
        />
      ),
      modules: ["orders"],
      operation: "read" as const,
    },
    {
      key: "Order Created",
      label: "Created",
      component: (
        <CreatedOrders
          setHeaderActions={setHeaderActions}
          setHeaderButtons={setHeaderButtons}
        />
      ),
      modules: ["orders"],
      operation: "read" as const,
    },
    {
      key: "Pick List",
      label: "Pick List",
      component: (
        <PickListPage
          key={pickListRefresh}
          setHeaderActions={setHeaderActions}
          setHeaderButtons={setHeaderButtons}
        />
      ),
      modules: ["orders"],
      operation: "read" as const,
    },
    {
      key: "Packaging",
      label: "Packaging",
      component: (
        <Packaging
          setHeaderActions={setHeaderActions}
          setHeaderButtons={setHeaderButtons}
        />
      ),
      modules: ["orders"],
      operation: "read" as const,
    },
    {
      key: "Pending",
      label: "Pending",
      component: (
        <PendingOrders
          setHeaderActions={setHeaderActions}
          setHeaderButtons={setHeaderButtons}
        />
      ),
      modules: ["orders"],
      operation: "read" as const,
    },
    {
      key: "Dispatched",
      label: "Dispatched",
      component: (
        <Dispatched
          setHeaderActions={setHeaderActions}
          setHeaderButtons={setHeaderButtons}
        />
      ),
      modules: ["orders"],
      operation: "read" as const,
    },
  ], [pickListRefresh]);

  const result = usePermissionTabs({
    tabs: ALL_TABS,
    param: "tab",
  });

  const visibleTabs = useMemo(() => {
    let tabs = result.visibleTabs;
    if (clientType === "LM") {
      tabs = tabs.filter((tab) => tab.key !== "Create Order");
    }
    return tabs;
  }, [clientType, result.visibleTabs]);

  const activeTabKey = searchParams.get("tab") || (visibleTabs[0]?.key || "");

  useEffect(() => {
    const isAllowed = visibleTabs.some((tab) => tab.key === activeTabKey);
    if (!isAllowed && visibleTabs.length > 0) {
      setSearchParams({ tab: visibleTabs[0].key }, { replace: true });
    }
  }, [activeTabKey, visibleTabs, setSearchParams]);

  const handleTabChange = (label: string) => {
    const tab = visibleTabs.find((t) => t.label === label);
    if (tab) {
      if (tab.key === "Pick List") {
        setPickListRefresh((prev) => prev + 1);
      }
      setSearchParams({ tab: tab.key });
    }
  };

  const activeTab = visibleTabs.find((tab) => tab.key === activeTabKey);
  // const content = activeTab?.component ?? visibleTabs[0]?.component;

  // Convert filters array to chips display
  const filterChips = useMemo(() => {
    const chips: Array<{
      heading: string;
      value: string;
      key: string;
      displayValue: string;
    }> = [];

    // Track seen search values to avoid duplicates for awbNumber, referenceNumber, mobileNumber
    const seenSearchValues = new Set<string>();
    let entryDateRange: { from?: string; to?: string } = {};
    let closeDateRange: { from?: string; to?: string } = {};

    filters.forEach((filter) => {
      const [key, value] = Object.entries(filter)[0] || [];
      if (key && value) {
        // For search-related fields, only show once per value
        if (
          key === "awbNumber" ||
          key === "referenceNumber" ||
          key === "mobileNumber"
        ) {
          if (seenSearchValues.has(value)) {
            return; // Skip duplicate
          }
          seenSearchValues.add(value);
        }
        if (key === "entryDateFrom") {
          entryDateRange.from = value;
          return;
        }
        if (key === "entryDateTo") {
          entryDateRange.to = value;
          return;
        }
        if (key === "closeDateFrom") {
          closeDateRange.from = value;
          return;
        }
        if (key === "closeDateTo") {
          closeDateRange.to = value;
          return;
        }
        const label = FILTER_LABEL_MAP[key] || key;
        chips.push({ heading: label, value, key, displayValue: value });
      }
    });
    if (entryDateRange.from || entryDateRange.to) {
      const display = [
        entryDateRange.from || "...",
        "→",
        entryDateRange.to || "...",
      ].join(" ");

      chips.push({
        heading: "Entry Date",
        value: display,
        key: "entryDateRange",
        displayValue: display,
      });
    }
    if (closeDateRange.from || closeDateRange.to) {
      const display = [
        closeDateRange.from || "...",
        "→",
        closeDateRange.to || "...",
      ].join(" ");

      chips.push({
        heading: "Close Date",
        value: display,
        key: "closeDateRange",
        displayValue: display,
      });
    }

    return chips;
  }, [filters]);

  const handleRemoveChip = (key: string, value: string) => {
    if (key === "entryDateRange") {
      setFilters((prev) =>
        prev.filter((f) => {
          const [k] = Object.entries(f)[0];
          return k !== "entryDateFrom" && k !== "entryDateTo";
        }),
      );
    } else if (key === "closeDateRange") {
      setFilters((prev) =>
        prev.filter((f) => {
          const [k] = Object.entries(f)[0];
          return k !== "closeDateFrom" && k !== "closeDateTo";
        }),
      );
    }

    setFilters((prev) =>
      prev.filter((f) => {
        const [filterKey, filterValue] = Object.entries(f)[0];

        // If removing a search chip, remove all three related filters (awb, ref, mobile) with that value
        if (
          key === "awbNumber" ||
          key === "referenceNumber" ||
          key === "mobileNumber"
        ) {
          if (
            filterValue === value &&
            (filterKey === "awbNumber" ||
              filterKey === "referenceNumber" ||
              filterKey === "mobileNumber")
          ) {
            return false; // Remove this filter
          }
        } else {
          // For non-search filters, remove only exact match
          if (filterKey === key && filterValue === value) {
            return false;
          }
        }

        return true;
      }),
    );
  };

  const handleResetAll = () => {
    setFilters([]);
  };

  const Chips = () => {
    if (filterChips.length === 0) return null;

    return (
      <div className="flex justify-between items-center">
        <div className="flex items-center flex-wrap gap-2">
          <p className="text-[#212121] text-base font-medium">
            Applied Filter :{" "}
          </p>
          {filterChips.map((chip, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#F7F6F2] rounded-full text-xs font-medium text-[#212121]"
            >
              <b>{chip.heading} :</b> {chip.displayValue}
              <span
                onClick={() => handleRemoveChip(chip.key, chip.value)}
                className="bg-[#212121]/5 hover:bg-[#212121]/20 rounded-full p-1 cursor-pointer transition"
              >
                <IconX size={14} />
              </span>
            </div>
          ))}
        </div>
        <button
          onClick={handleResetAll}
          className="bg-[#303030] text-white px-4 py-2 text-sm rounded-full flex-shrink-0 hover:bg-[#404040] transition"
        >
          Reset All
        </button>
      </div>
    );
  };

  return (
    <div className="px-4 md:px-6 py-2 space-y-4 pb-8">
      <Chips />
      <PermissionTabManager
        visibleTabs={visibleTabs}
        activeLabel={activeTab?.label || ""}
        content={activeTab?.component || null}
        isLoading={result.isLoading}
        error={result.error}
        handleChange={handleTabChange}
        className=""
        activeClassName="bg-[#000] text-[#FFFFFF] font-bold shadow-sm"
        headerButtons={
          (headerActions || headerButtons) ? (
            <div className="flex items-center gap-2">
              {headerActions}
              {headerButtons}
            </div>
          ) : undefined
        }
      />
    </div>
  );
};

export default OrderManagement;
